import { CircularProgress, Collapse } from "@mui/material";
import Joi from "joi";
import { useEffect, useState } from "react";
import { FcCollapse, FcExpand } from "react-icons/fc";
import { IoArrowBackSharp } from "react-icons/io5";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { Col, Row } from "reactstrap";
import DigitalSignature from "../../components/Assistances/DigitalSignature";
import ErrorMessageComponent from "../../components/ErrorMessageComponent";
import InputComponent from "../../components/InputComponent";
import Layout from "../../components/Layout/Layout";
import { SelectItemsProps } from "../../components/Select2Component";
import SelectComponent from "../../components/SelectComponent";
import { useAuth } from "../../context/AuthContext";
import { useAvailableModulesContext } from "../../context/AvailableModulesContext";
import Toast from "../../helpers/Toast";
import { Utilities } from "../../helpers/Utilities";
import { DigitalSignatureDTO } from "../../models/Assistance/DigitalSignatureDTO";
import { PasswordUpdateDTO } from "../../models/User/PasswordUpdateDTO";
import { RoleDTO } from "../../models/User/RoleDTO";
import { RoleListCheckbox } from "../../models/User/RoleListCheckboxDTO";
import { UserDetailDTO } from "../../models/User/UserDetailDTO";
import { UserStatusUpdateDTO } from "../../models/User/UserStatusUpdateDTO";
import { UserUpdateDTO } from "../../models/User/UserUpdateDTO";
import { AdminService } from "../../services/AdminService";
import { AuthService } from "../../services/AuthService";
import { SubcontractorService } from "../../services/SubcontractorService";
import { UserService } from "../../services/UserService";
import { SearchByCompanyNameDTO } from "../../models/Subcontractor/SearchByCompanyNameDTO";
import SearchSelectComponent from "../../components/SearchSelectComponent";


export default function UserEdit() {
    const intl = useIntl();
    const { currentUser, isAdmin, logoutUser } = useAuth();
    const navigate = useNavigate();
    const [userData, setUserData] = useState<UserDetailDTO | null>(null);
    const userService = new UserService();
    const adminService = new AdminService();
    const { id } = useParams<{ id: string; }>();
    const [roleList, setRoleList] = useState<RoleListCheckbox[]>([]);

    //Loadings
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingNewUpdate, setLoadingNewUpdate] = useState<boolean>(false);
    const [loadingUpdatePassword, setLoadingUpdatePassword] = useState<boolean>(false);
    const [loadingUpdateUserStatus, setLoadingUpdateUserStatus] = useState<boolean>(false);

    //Variáveis para controlar os collapses e expands das secções
    const [userIsOpen, setUserIsOpen] = useState<boolean>(true);
    const [passwordIsOpen, setPasswordIsOpen] = useState<boolean>(true);
    const [roleIsOpen, setRoleIsOpen] = useState<boolean>(true);

    const [inputName, setInputName] = useState<string>("");
    const [inputEmail, setInputEmail] = useState<string>("");
    const [inputActive, setInputActive] = useState<string>("");
    const [inputOldPassword, setInputOldPassword] = useState<string>("");
    const [inputNewPassword, setInputNewPassword] = useState<string>("");
    const [inputNewPassword2, setInputNewPassword2] = useState<string>("");
    const [preferedView, setPreferedView] = useState<string>("");
    const [listPreferedViews, setListPreferedViews] = useState<SelectItemsProps[]>([]);
    const [canChangePreferedView, setCanChangePreferedView] = useState<boolean>(false);

    const [validationResultPassword, setValidationResultPassword] = useState<any[]>([]);
    const [validationResultUser, setValidationResultUser] = useState<any[]>([]);
    const [validationResultActiveSelect, setValidationResultActiveSelect] = useState<any[]>([]);

    const [isSignatureOpen, setIsSignatureOpen] = useState(false); // assinatura digital

    const subcontractorService = new SubcontractorService();
    const [subcontractorSelected, setSubcontractorSelected] = useState<SelectItemsProps | null>(null);
    const [subcontractors, setSubcontractors] = useState<SelectItemsProps[]>([]);

    const { availableModules, assistanceTypes } = useAvailableModulesContext();
    const [isActiveOutsourcingModule, setIsActiveOutsourcingModule] = useState<boolean>(false);

    useEffect(() => {
        if (availableModules.includes("IsActiveOutsourcingModule")) {
            setIsActiveOutsourcingModule(true);
        }
    }, [availableModules]);

    const schemaPasswordValidation = Joi.object({
        newPassword: Joi.string()
            .pattern(Utilities.PasswordRegularExpression)
            .min(8)
            .required()
            .messages({
                "any.required": intl.formatMessage({
                    id: "Screens.Auth.Validation.PasswordRequired",
                }),
                "string.base": intl.formatMessage({
                    id: "Screens.Auth.Validation.PasswordRequired",
                }),
                "string.empty": intl.formatMessage({
                    id: "Screens.Auth.Validation.PasswordRequired",
                }),
                "string.min": intl.formatMessage({ id: "Screens.Auth.Validation.PasswordMin" }),
                "string.pattern.base": intl.formatMessage({
                    id: "Screens.Auth.Validation.PasswordFormat",
                }),
            }),
        newPassword2: Joi.string()
            .valid(Joi.ref("newPassword"))
            .required()
            .messages({
                "any.required": intl.formatMessage({
                    id: "Screens.Auth.Validation.PasswordRequired",
                }),
                "string.base": intl.formatMessage({
                    id: "Screens.Auth.Validation.PasswordRequired",
                }),
                "string.empty": intl.formatMessage({
                    id: "Screens.Auth.Validation.PasswordRequired",
                }),
                "any.only": intl.formatMessage({ id: "Screens.Auth.Validation.PasswordSame" }),
            }),
        oldPassword: Joi.string()
            .required()
            .messages({
                "any.required": intl.formatMessage({
                    id: "Screens.Auth.Validation.PasswordRequired",
                }),
                "string.base": intl.formatMessage({
                    id: "Screens.Auth.Validation.PasswordRequired",
                }),
                "string.empty": intl.formatMessage({
                    id: "Screens.Auth.Validation.PasswordRequired",
                }),
            }),
    });

    const schemaUserValidation = Joi.object({
        name: Joi.string()
            .required()
            .messages({
                "any.required": intl.formatMessage({ id: "Screens.Auth.Validation.NameRequired" }),
                "string.base": intl.formatMessage({ id: "Screens.Auth.Validation.NameRequired" }),
                "string.empty": intl.formatMessage({ id: "Screens.Auth.Validation.NameRequired" }),
            }),
        email: Joi.string()
            .required()
            .messages({
                "any.required": intl.formatMessage({ id: "Screens.Auth.Validation.EmailRequired" }),
                "string.base": intl.formatMessage({ id: "Screens.Auth.Validation.EmailRequired" }),
                "string.empty": intl.formatMessage({ id: "Screens.Auth.Validation.EmailRequired" }),
            }),
    });

    const schemaActiveValidation = Joi.object({
        active: Joi.string()
            .required()
            .messages({
                "any.required": intl.formatMessage({
                    id: "Screens.UserEdit.Validation.UserStatus",
                }),
                "string.base": intl.formatMessage({ id: "Screens.UserEdit.Validation.UserStatus" }),
                "string.empty": intl.formatMessage({
                    id: "Screens.UserEdit.Validation.UserStatus",
                }),
            }),
    });

    useEffect(() => {
        loadUser(id ?? '');
    }, [id]);

    useEffect(() => {
        //Apenas vai buscar os roles se o utilizador for administrador
        //E se os dados do utilizador já tiverem sido carregados
        if (userData !== null) {
            if (checkIfCanChangePreferedView() === true) {
                setCanChangePreferedView(true);
                loadPreferedViews();
            }

            if (isAdmin() === true) {
                loadRoles();
            }
        }
    }, [userData]);

    const loadUser = async (id: string) => {
        var response = await userService.GetById(id);

        if (response.success !== true) {
            Toast.Show("error", "Erro ao carregar os dados do utilizador!");
            setLoading(false);
            setUserData(null);
            return;
        }

        if (response.obj == null) {
            Toast.Show("error", "Utilizador não existe!");
            setLoading(false);
            setUserData(null);
            return;
        }
        setUserData(response.obj);
        setInputName(response.obj.name);
        setInputEmail(response.obj.email);
        setInputActive(response.obj.active ? "True" : "False");
        setPreferedView(response.obj.preferedView);
        setSubcontractorSelected({
            value: String(response.obj.subcontractor?.id!),
            label: response.obj.subcontractor ? `${response.obj.subcontractor.companyName}` : ""
        });
        setLoading(false);
    };

    const goBack = () => {
        navigate(-1);
    };

    const loadRoles = async () => {
        var response = await adminService.GetRoles();
        if (response.success === true && response.obj != null) {
            var items: RoleListCheckbox[] = [];
            var checked: boolean = false;

            response.obj.forEach((s) => {
                checked = false;
                var role = userData?.role.find((role) => role.id === s.id);
                if (role != null) {
                    checked = true;
                }

                items.push({
                    name: s.name === "Técnico Oficina" ? 'Técnico' : s.name,
                    id: s.id,
                    isChecked: checked,
                    isDisabled: false,
                });
            });

            setRoleList(items);
        }
    };

    const handleRoleCheckbox = (e: any, i: number) => {
        let newList: RoleListCheckbox[] = roleList;
        var rolesToDisable = Utilities.RolesToDisable(newList, e.target.value);

        newList?.map((i) => {
            var currentRoleName = i.name.toUpperCase();

            //Se o role atual existir na lista dos roles por bloquear
            i.isDisabled = rolesToDisable.findIndex((r) => r === currentRoleName) !== -1;

            if (i.id === e.target.value) {
                var isChecked: boolean = !i.isChecked;
                i.isChecked = isChecked;
            }
            return i;
        });
        setRoleList(newList?.filter((i) => i.id !== "-1"));
    };

    const statusList: SelectItemsProps[] = [
        {
            label: intl.formatMessage({ id: "Screens.Users.Table.Active" }),
            value: "True",
        },
        {
            label: intl.formatMessage({ id: "Screens.Users.Table.Inactive" }),
            value: "False",
        },
    ];

    const canEditEmail = (): boolean => {
        if (isAdmin() === false) return false;
        if (currentUser == null) return false;
        if (currentUser.id === id) return false;
        return true;
    };

    const validateUser = (updatedUser: UserUpdateDTO) => {
        return new Promise(function (resolve) {
            var validationResult = schemaUserValidation.validate(
                {
                    name: updatedUser.name,
                    email: updatedUser.email,
                },
                { abortEarly: false },
            );
            resolve(validationResult);
        });
    };

    const getSubcontractors = async (
        companyName: string
    ): Promise<SelectItemsProps[]> => {
        var companyNameDTO: SearchByCompanyNameDTO = {
            companyName: companyName
        };
        const response = await subcontractorService.GetByCompanyName(companyNameDTO);
        if (response.success === false || response.obj === null) {
            Toast.Show("error", response.message);
        }
        const list: SelectItemsProps[] = [];
        response.obj?.forEach((element) => {
            var item: SelectItemsProps = {
                label: `${element.companyName}`,
                value: `${element.id}`
            }
            list.push(item);
        });
        setSubcontractors(list);
        return list;
    };

    const updateUser = async () => {
        const updatedUser: UserUpdateDTO = {
            id: id ?? '',
            name: inputName,
            email: inputEmail,
            preferedView: preferedView,
            technicianCode: userData?.technicianCode === undefined ? null : userData?.technicianCode,
            subcontractorId: subcontractorSelected != null ? Number(subcontractorSelected.value) : null
        };

        var validate: any = await validateUser(updatedUser);
        if (validate.error != null) {
            setValidationResultUser(validate.error.details);
            return;
        } else {
            setValidationResultUser([]);
        }

        setLoadingNewUpdate(true);
        const response = await userService.UpdateUser(id ?? '', updatedUser);
        setLoadingNewUpdate(false);

        if (response.success !== true) {
            Toast.Show("error", response.message);
            return;
        }

        Toast.Show("success", response.message);

        loadUser(id!.toString());

        var canChangeView = checkIfCanChangePreferedView();
        if (canChangeView === true && id == currentUser!.id) {
            changeView();
        }
    };

    const validatePassword = (updatedPassword: PasswordUpdateDTO, repeatPassword: string) => {
        return new Promise(function (resolve) {
            var validationResult = schemaPasswordValidation.validate(
                {
                    oldPassword: updatedPassword.oldPassword,
                    newPassword: updatedPassword.newPassword,
                    newPassword2: repeatPassword,
                },
                { abortEarly: false },
            );
            resolve(validationResult);
        });
    };

    const validateActiveSelect = (activeSelect: string) => {
        return new Promise(function (resolve) {
            var validationResult = schemaActiveValidation.validate(
                {
                    active: activeSelect,
                },
                { abortEarly: false },
            );
            resolve(validationResult);
        });
    };

    const updatePassword = async () => {
        setLoadingUpdatePassword(true);

        const updatedPassword: PasswordUpdateDTO = {
            id: id ?? '',
            oldPassword: inputOldPassword,
            newPassword: inputNewPassword,
        };

        validatePassword(updatedPassword, inputNewPassword2).then(async (result: any) => {
            if (result.error != null) {
                setValidationResultPassword(result.error.details);
            } else {
                setValidationResultPassword([]);

                const response = await userService.UpdatePassword(id ?? '', updatedPassword);

                if (response.success !== true) {
                    Toast.Show("error", response.message);
                    setLoadingUpdatePassword(false);
                    return;
                }

                setInputOldPassword("");
                setInputNewPassword("");
                setInputNewPassword2("");
                Toast.Show("success", response.message);
            }
            setLoadingUpdatePassword(false);
        });
    };

    const updateUserStatus = () => {
        setLoadingUpdateUserStatus(true);
        validateActiveSelect(inputActive).then(async (result: any) => {
            if (result.error != null) {
                setValidationResultActiveSelect(result.error.details);
            } else {
                setValidationResultActiveSelect([]);

                //Filtar na role list os que não tem o isChecked a true, e depois mapear para RoleDTO
                const roleListHelper = roleList
                    ?.filter((i) => i.isChecked !== false)
                    .map((obj) => ({ id: obj.id, name: obj.name === 'Técnico' ? 'Técnico Oficina' : obj.name } as RoleDTO));

                const updatedUserStatus: UserStatusUpdateDTO = {
                    id: id ?? '',
                    active: inputActive === "True" ? true : false,
                    userRoles: roleListHelper ? roleListHelper : [],
                };
                const response = await adminService.UpdateUserStatus(id ?? '', updatedUserStatus);

                if (response.success !== true) {
                    Toast.Show("error", response.message);
                    setLoadingUpdateUserStatus(false);
                    return;
                }
                Toast.Show("success", response.message);
            }
            setLoadingUpdateUserStatus(false);
        });
    };

    const loadPreferedViews = async () => {
        var resultGetPreferedViews = await userService.GetPreferedViews();

        if (resultGetPreferedViews.success == false) {
            Toast.Show("error", resultGetPreferedViews.message);
            return;
        }

        var items: SelectItemsProps[] = [];


        resultGetPreferedViews.obj.forEach(element => {
            items.push({
                label: element === 'Oficina' ? 'Técnico' : 'Backoffice',
                value: element
            });
        });

        setListPreferedViews(items);
    }

    const checkIfCanChangePreferedView = () => {
        if (isAdmin() === true) {
            return true;
        }

        if (userData!.role.length > 1 && userData?.role.findIndex(a => a.name.toUpperCase() !== 'Técnico Oficina'.toUpperCase()) !== -1) {
            return true;
        }

        return false;
    }

    const changeView = () => {
        if (preferedView != currentUser?.preferedView) {
            const authService = new AuthService();
            authService.Logout();
            logoutUser(currentUser!);
            navigate("/login");
        }
    }


    // Bloco assinatura digital
    const handleSignatureSave = async (signatureData: DigitalSignatureDTO) => {
        // Chama a API com a assinatura
        const response = await userService.SubmitDigitalSignature(id, signatureData);
        if (response === null || !response.success) {
            Toast.Show("error", response.message);
        }

        Toast.Show("success", intl.formatMessage({ id: "TechnicianEdit.SignSuccess" }));
    }

    const handleSignatureCancel = () => {
        setIsSignatureOpen(false);
    };

    const handleSignatureDialog = () => {
        setIsSignatureOpen(!isSignatureOpen);
    };

    return (
        <Layout>
            <div className="pageContainer">
                <div className="pageHeader">
                    <span className="pageTitle">
                        {" "}
                        <IoArrowBackSharp className="pageTitleBackIcon" onClick={goBack} />
                        <FormattedMessage id="Screens.User.Title" />:
                    </span>
                </div>

                <div className="pageBody">
                    {loading === true && (
                        <div className="loading">
                            <span>
                                {" "}
                                <FormattedMessage id="Common.Loading.DataLoading" />
                                ...{" "}
                            </span>
                            <CircularProgress style={{ marginLeft: "10px" }} size={"25px"} />
                        </div>
                    )}

                    {loading === false && userData == null && (
                        <div style={{ color: "#E74C3C" }}>
                            <FormattedMessage id="Screens.User.UserDontExist" />
                        </div>
                    )}

                    {loading === false && userData != null && (
                        <div>
                            <div className="pageComponent">
                                <Row style={{ margin: "0px" }}>
                                    <span
                                        className="pageSubHeaderBlockGray"
                                        onClick={() => setUserIsOpen(!userIsOpen)}
                                    >
                                        <FormattedMessage id="Screens.User.Title" />
                                        <div className="CollapseIcon">
                                            {" "}
                                            {userIsOpen ? <FcCollapse /> : <FcExpand />}
                                        </div>
                                    </span>
                                    <Collapse in={userIsOpen} unmountOnExit>
                                        <Row>
                                            <InputComponent
                                                xl={2}
                                                lg={2}
                                                md={6}
                                                sm={12}
                                                xs={12}
                                                style={{ width: "100%" }}
                                                type={"text"}
                                                value={inputName}
                                                label={intl.formatMessage({
                                                    id: "Screens.User.Info.Name",
                                                })}
                                                readOnly={!isAdmin() ? true : false}
                                                onChange={(t: string) => setInputName(t)}
                                            />
                                            <InputComponent
                                                xl={3}
                                                lg={3}
                                                md={6}
                                                sm={12}
                                                xs={12}
                                                style={{ width: "100%" }}
                                                type={"text"}
                                                value={inputEmail}
                                                label={intl.formatMessage({
                                                    id: "Screens.User.Info.Email",
                                                })}
                                                readOnly={!canEditEmail()}
                                                onChange={(t: string) => setInputEmail(t)}
                                            />

                                            {canChangePreferedView === true &&
                                                <SelectComponent xl={2} lg={2} md={6} sm={12} xs={12}
                                                    style={{ width: "100%" }}
                                                    selectedItemValue={preferedView}
                                                    label={intl.formatMessage({ id: "Screens.User.Info.VistaPreferida" })}
                                                    items={listPreferedViews}
                                                    onChange={(t: string) => setPreferedView(t)} />
                                            }

                                            <InputComponent
                                                xl={2}
                                                lg={2}
                                                md={6}
                                                sm={12}
                                                xs={12}
                                                style={{ width: "100%" }}
                                                type={"text"}
                                                value={userData.technicianCode ?? ""}
                                                label={intl.formatMessage({
                                                    id: "Screens.User.Info.TechnicianCode",
                                                })}
                                                readOnly={false}
                                                onChange={(t: string) => setUserData({ ...userData, technicianCode: t })}
                                            />
                                            <Col xl={2} lg={3} md={4} sm={12} xs={12} style={{
                                                marginTop: "30px",
                                                minWidth: "70px",
                                            }}>
                                                <button
                                                    className="button"
                                                    style={{ margin: 0, width: "100%" }}
                                                    onClick={() => handleSignatureDialog()}
                                                >
                                                    <FormattedMessage id="TechnicianEdit.Signature" />
                                                </button>
                                            </Col>
                                            {isSignatureOpen && (
                                                <DigitalSignature
                                                    onCancel={handleSignatureCancel}
                                                    onSave={(dataUrl: string) => {
                                                        const signatureData: DigitalSignatureDTO = {
                                                            signature: dataUrl,
                                                        };
                                                        handleSignatureSave(signatureData);
                                                    }}
                                                />
                                            )}
                                        </Row>


                                        {validationResultUser.length !== 0 && (
                                            <Row>
                                                <Col xl={3} lg={4} md={5} sm={12} xs={12}>
                                                    <ErrorMessageComponent
                                                        validationResult={validationResultUser}
                                                        fieldToValidate="name"
                                                    />
                                                </Col>
                                                <Col xl={4} lg={4} md={5} sm={12} xs={12}>
                                                    <ErrorMessageComponent
                                                        validationResult={validationResultUser}
                                                        fieldToValidate="email"
                                                    />
                                                </Col>
                                            </Row>
                                        )}

                                        <Row>
                                            {loadingNewUpdate === true ? (
                                                <Col
                                                    className="circularProgressForm"
                                                    xl={1}
                                                    lg={2}
                                                    md={4}
                                                    sm={12}
                                                    xs={12}
                                                    style={{ marginTop: "0.8em" }}
                                                >
                                                    <CircularProgress size={"25px"} />
                                                </Col>
                                            ) : (
                                                <Col
                                                    xl={2}
                                                    lg={3}
                                                    md={4}
                                                    sm={12}
                                                    xs={12}
                                                    style={{ marginTop: "1em" }}
                                                >
                                                    <button
                                                        className="button"
                                                        style={{ margin: 0, width: "100%" }}
                                                        onClick={() => updateUser()}
                                                    >
                                                        <FormattedMessage id="Screens.User.Button.Update" />
                                                    </button>
                                                </Col>
                                            )}

                                            {isActiveOutsourcingModule && (
                                                <>
                                                    <SearchSelectComponent
                                                        xl={5}
                                                        lg={5}
                                                        md={6}
                                                        sm={12}
                                                        xs={12}
                                                        label={intl.formatMessage({
                                                            id: "Screens.Users.Info.Subcontractor",
                                                        })}
                                                        loadOptions={getSubcontractors}
                                                        onChange={(e) => {
                                                            setSubcontractorSelected(e);
                                                        }}
                                                        value={subcontractorSelected}
                                                        style={{ width: "100%" }}
                                                    />
                                                </>
                                            )}
                                        </Row>
                                    </Collapse>
                                </Row>
                            </div>
                            {currentUser?.id === id && (
                                <div className="pageComponent">
                                    <Row style={{ margin: "0px" }}>
                                        <span
                                            className="pageSubHeaderBlockGray"
                                            onClick={() => setPasswordIsOpen(!passwordIsOpen)}
                                        >
                                            <FormattedMessage id="Screens.User.SubTitle.Password" />
                                            <div className="CollapseIcon">
                                                {" "}
                                                {passwordIsOpen ? <FcCollapse /> : <FcExpand />}
                                            </div>
                                        </span>
                                        <Collapse in={passwordIsOpen} unmountOnExit>
                                            <Row>
                                                <InputComponent
                                                    xl={3}
                                                    lg={4}
                                                    md={5}
                                                    sm={12}
                                                    xs={12}
                                                    style={{ width: "100%" }}
                                                    type={"password"}
                                                    value={inputOldPassword}
                                                    label={intl.formatMessage({
                                                        id: "Screens.User.Info.OldPassword",
                                                    })}
                                                    readOnly={false}
                                                    onChange={(t: string) => setInputOldPassword(t)}
                                                />
                                                <InputComponent
                                                    xl={3}
                                                    lg={4}
                                                    md={5}
                                                    sm={12}
                                                    xs={12}
                                                    style={{ width: "100%" }}
                                                    type={"password"}
                                                    value={inputNewPassword}
                                                    label={intl.formatMessage({
                                                        id: "Screens.User.Info.NewPassword",
                                                    })}
                                                    readOnly={false}
                                                    onChange={(t: string) => setInputNewPassword(t)}
                                                />
                                                <InputComponent
                                                    xl={3}
                                                    lg={4}
                                                    md={5}
                                                    sm={12}
                                                    xs={12}
                                                    style={{ width: "100%" }}
                                                    type={"password"}
                                                    value={inputNewPassword2}
                                                    label={intl.formatMessage({
                                                        id: "Screens.User.Info.NewPassword2",
                                                    })}
                                                    readOnly={false}
                                                    onChange={(t: string) =>
                                                        setInputNewPassword2(t)
                                                    }
                                                />
                                            </Row>
                                            {validationResultPassword.length !== 0 && (
                                                <Row>
                                                    <Col xl={3} lg={4} md={5} sm={12} xs={12}>
                                                        <ErrorMessageComponent
                                                            validationResult={
                                                                validationResultPassword
                                                            }
                                                            fieldToValidate="oldPassword"
                                                        />
                                                    </Col>
                                                    <Col xl={3} lg={4} md={5} sm={12} xs={12}>
                                                        <ErrorMessageComponent
                                                            validationResult={
                                                                validationResultPassword
                                                            }
                                                            fieldToValidate="newPassword"
                                                        />
                                                    </Col>
                                                    <Col xl={3} lg={4} md={5} sm={12} xs={12}>
                                                        <ErrorMessageComponent
                                                            validationResult={
                                                                validationResultPassword
                                                            }
                                                            fieldToValidate="newPassword2"
                                                        />
                                                    </Col>
                                                </Row>
                                            )}

                                            <Row>
                                                {loadingUpdatePassword === true ? (
                                                    <Col
                                                        className="circularProgressForm"
                                                        xl={1}
                                                        lg={2}
                                                        md={4}
                                                        sm={12}
                                                        xs={12}
                                                        style={{ marginTop: "0.8em" }}
                                                    >
                                                        <CircularProgress size={"25px"} />
                                                    </Col>
                                                ) : (
                                                    <Col
                                                        xl={2}
                                                        lg={3}
                                                        md={4}
                                                        sm={12}
                                                        xs={12}
                                                        style={{ marginTop: "1em" }}
                                                    >
                                                        <button
                                                            className="button"
                                                            style={{ margin: 0, width: "100%" }}
                                                            onClick={() => updatePassword()}
                                                        >
                                                            <FormattedMessage id="Screens.User.Button.UpdatePassword" />
                                                        </button>
                                                    </Col>
                                                )}
                                            </Row>
                                        </Collapse>
                                    </Row>
                                </div>
                            )}

                            {isAdmin() && (
                                <div className="pageComponent">
                                    <Row style={{ margin: "0px" }}>
                                        <span
                                            className="pageSubHeaderBlockGray"
                                            onClick={() => setRoleIsOpen(!roleIsOpen)}
                                        >
                                            <FormattedMessage id="Screens.User.SubTitle.AdminArea" />
                                            <div className="CollapseIcon">
                                                {" "}
                                                {roleIsOpen ? <FcCollapse /> : <FcExpand />}
                                            </div>
                                        </span>
                                        <Collapse in={roleIsOpen} unmountOnExit>
                                            <Row style={{ marginBottom: "15px" }}>
                                                <SelectComponent
                                                    xl={2}
                                                    lg={4}
                                                    md={5}
                                                    sm={12}
                                                    xs={12}
                                                    style={{ width: "100%" }}
                                                    label={intl.formatMessage({
                                                        id: "Screens.User.Info.Status",
                                                    })}
                                                    selectedItemValue={inputActive}
                                                    items={statusList}
                                                    onChange={(t: string) => {
                                                        setInputActive(t);
                                                    }}
                                                />
                                            </Row>

                                            {validationResultActiveSelect.length !== 0 && (
                                                <Row>
                                                    <Col xl={2} lg={4} md={5} sm={12} xs={12}>
                                                        <ErrorMessageComponent
                                                            validationResult={
                                                                validationResultActiveSelect
                                                            }
                                                            fieldToValidate="active"
                                                        />
                                                    </Col>
                                                </Row>
                                            )}
                                            <span className="label">
                                                <FormattedMessage id="Screens.User.Info.Roles" />:
                                            </span>
                                            <div className="form-check">
                                                {roleList?.map((item, i) => {
                                                    return (
                                                        <li key={item.id}>
                                                            <label
                                                                className={
                                                                    item.isDisabled === true
                                                                        ? "form-check-label-disabled"
                                                                        : "form-check-label"
                                                                }
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    disabled={item.isDisabled}
                                                                    key={item.id}
                                                                    value={item.id}
                                                                    checked={item.isChecked}
                                                                    onChange={(e) =>
                                                                        handleRoleCheckbox(e, i)
                                                                    }
                                                                    className="form-check-input"
                                                                />
                                                                {item.name}
                                                            </label>
                                                        </li>
                                                    );
                                                })}
                                            </div>

                                            <Row>
                                                {loadingUpdateUserStatus === true ? (
                                                    <Col
                                                        className="circularProgressForm"
                                                        xl={1}
                                                        lg={2}
                                                        md={4}
                                                        sm={12}
                                                        xs={12}
                                                        style={{ marginTop: "0.8em" }}
                                                    >
                                                        <CircularProgress size={"25px"} />
                                                    </Col>
                                                ) : (
                                                    <Col
                                                        xl={2}
                                                        lg={3}
                                                        md={4}
                                                        sm={12}
                                                        xs={12}
                                                        style={{ marginTop: "1em" }}
                                                    >
                                                        <button
                                                            className="button"
                                                            style={{ margin: 0, width: "100%" }}
                                                            onClick={() => updateUserStatus()}
                                                        >
                                                            <FormattedMessage id="Screens.User.Button.UpdateRoles" />
                                                        </button>
                                                    </Col>
                                                )}
                                            </Row>
                                        </Collapse>
                                    </Row>
                                </div>
                            )}

                            {/* Código do dispositivo  */}

                            {/* {userData.id === currentUser?.id ? (<div className="pageComponent">
                                <Row style={{ margin: "0px" }}>
                                    <span
                                        className="pageSubHeaderBlockGray"
                                        onClick={() => setTabletIdIsOpen(!tabletIdIsOpen)}
                                    >
                                        <FormattedMessage id="Screens.User.Info.CodigoTablet" />
                                        <div className="CollapseIcon">
                                            {" "}
                                            {tabletIdIsOpen ? <FcCollapse /> : <FcExpand />}
                                        </div>
                                    </span>
                                    <Collapse in={tabletIdIsOpen} unmountOnExit>
                                        <span className="label" style={{ fontSize: "1rem" }}>
                                            {typeof cookies["tabletId"] !== 'undefined' && cookies["tabletId"] != null ? (
                                                <>{cookies["tabletId"]}</>
                                            ) : (
                                                <span style={{ color: "red" }}>Erro ao obter código do dispositivo. Por favor, volte a fazer login.</span>
                                            )}
                                        </span>
                                    </Collapse>
                                </Row>
                            </div>) : ""} */}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}
